import { StoryblokComponent } from '@storyblok/react';
import { useMemo } from 'react';

import { useRouter } from 'next/router';

import { useGlobalProviderContext } from '../../../context/global';
import { JsonObject } from '../../../types/jsonObject';
import {
  ServiceOSApiClient,
  ServiceOSRegisterRequestPayload,
} from '../../../utils/serviceOSApiClient';
import { FormBuilderRequestLifecycleContext } from '../../FormBuilder/FormBuilder.types';
import { FormBuilderDefaultFieldValue } from '../../FormBuilder/hooks/useFormBuilderState';
import { ProSignUpFormProps } from './ProSignUpForm.types';
import { POST_SIGN_UP_SERVICEOS_EMAIL_QUERY_PARAM } from './ProSignUpSuccessMessageBlock/ProSignUpSuccessMessageBlock';

const serviceOSApiClient = new ServiceOSApiClient();

const pickQueryStringParam = (
  query: Record<string, string | string[] | undefined>,
  names: string[]
): string => {
  for (const name of names) {
    const raw = query[name];
    const v = Array.isArray(raw) ? raw[0] : raw;
    if (typeof v === 'string' && v.trim()) {
      return v.trim();
    }
  }
  return '';
};

export const ProSignUpForm = (props: ProSignUpFormProps) => {
  const { blok } = props;
  const { form } = blok;

  const globalContext = useGlobalProviderContext();
  const router = useRouter();

  const referrerCodeFromUrl = useMemo(
    () => pickQueryStringParam(router.query, ['referral_code', 'referrer_code', 'ref']),
    [router.query]
  );

  const defaultFieldValues = useMemo((): FormBuilderDefaultFieldValue[] => {
    if (!referrerCodeFromUrl) {
      return [];
    }
    return [
      {
        key: 'referrer_code',
        value: referrerCodeFromUrl,
        includeInSubmitRequest: true,
      },
    ];
  }, [referrerCodeFromUrl]);

  if (!form.length) {
    return null;
  }

  const handleSubmit = async (data: JsonObject, context: FormBuilderRequestLifecycleContext) => {
    const payload: ServiceOSRegisterRequestPayload = {
      first_name: data.first_name as string,
      last_name: data.last_name as string,
      email: data.email as string,
      ...(data.phone && { phone: String(data.phone) }),
      password: data.password as string,
      referrer_code: data.referrer_code as string,
      ...(data.allow_mk_all && {
        preferences: {
          allow_mk_all: 1,
        },
      }),
    };
    const { successMessage, errorMessage } = await serviceOSApiClient.register(
      payload,
      router.locale
    );

    if (errorMessage) {
      context.setErrorMessage(errorMessage);
    } else if (successMessage) {
      context.setSuccessMessage(successMessage);

      if (globalContext.serviceOSSettings.postSignUpPageUrl) {
        const url = new URL(
          globalContext.serviceOSSettings.postSignUpPageUrl,
          window.location.origin
        );
        url.searchParams.set(POST_SIGN_UP_SERVICEOS_EMAIL_QUERY_PARAM, data.email as string);

        void router.push(url);
      }
    }
  };

  const formBuilderBlok = {
    ...form[0],
    submitDataAs: 'json',
  };

  return (
    <StoryblokComponent
      blok={formBuilderBlok}
      key={formBuilderBlok._uid}
      onSubmit={handleSubmit}
      defaultFieldValues={defaultFieldValues}
    />
  );
};
